/* eslint-disable @typescript-eslint/no-explicit-any */
import { get } from "@/shared/utils/fetchApi";
import { AgentExecutionResponse } from "../types/AgentExecutionResponse";

// ===== INTERFACES E TIPOS =====

/** One event in the per-node processing log. Mirrors the backend ProcessingLogEntry. */
export interface ProcessingLogEntry {
  timestamp: string;
  level: "debug" | "info" | "warn" | "error";
  message: string;
  meta?: Record<string, unknown>;
}

/**
 * Interface para logs de execução de nós
 * Representa o log de um nó específico durante uma execução
 */
export interface ExecutionNodeLog {
  id: string;
  execution_id: string;
  node_id: string;
  node_type: string;
  /** Human-readable title from the workflow definition. Null on legacy rows. */
  node_title: string | null;
  status: string;
  input: any;
  output: any;
  error_message: string | null;
  /** Falha tolerada (continue_on_failure): status 'completed' + warning. Null on legacy rows. */
  warning_message?: string | null;
  /** Null em nós ainda não iniciados (v2 emite null; legacy idem em rows pending). */
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  node_schema: any;
  /** Timeline of events captured during this node's execution. Null on legacy rows. */
  processing_log: ProcessingLogEntry[] | null;
}

// ===== FUNÇÕES DE EXECUÇÕES =====

/**
 * Obtém as execuções de um agente com paginação
 * @param agentBaseId ID da base do agente
 * @param limit Limite de resultados por página
 * @param offset Deslocamento para paginação
 * @returns Promise com a resposta das execuções
 */
export async function getAgentExecutions(
  agentBaseId: string,
  limit = 10,
  offset = 0,
): Promise<AgentExecutionResponse> {
  return get<AgentExecutionResponse>(
    `/agent-bases/${agentBaseId}/executions?limit=${limit}&offset=${offset}`,
  );
}

// ===== FUNÇÕES DE LOGS DE EXECUÇÃO =====

/**
 * Obtém os logs de execução de nós para uma execução específica
 * @param executionId ID da execução
 * @returns Promise com array de logs ordenados cronologicamente
 */
export async function getExecutionNodeLogs(
  executionId: string,
): Promise<ExecutionNodeLog[]> {
  // Sem try/catch: o erro sobe direto. Havia só um `console.error` antes do
  // `throw`, o que registrava a mesma falha duas vezes — aqui e em quem chamou.
  // `/executions/:id/node-logs` retorna array flat (sem {success, data}).
  const rows =
    (await get<ExecutionNodeLog[]>(`/executions/${executionId}/node-logs`)) ??
    [];

  // Ordenar do mais antigo para o mais recente por started_at; nós ainda não
  // iniciados (started_at null) vão pro topo (epoch 0 — mesmo comportamento
  // que new Date(null) produzia implicitamente).
  return rows.sort((a: ExecutionNodeLog, b: ExecutionNodeLog) => {
    const dateA = a.started_at ? new Date(a.started_at).getTime() : 0;
    const dateB = b.started_at ? new Date(b.started_at).getTime() : 0;
    return dateA - dateB;
  });
}
